import React, { useState, useEffect } from "react";
import { adminApiFetch, normalizeVillas } from "./api";

function Villas() {
    const baseUrl = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_BASE_URL;
    const [villas, setVillas] = useState([]);
    const [editedVillas, setEditedVillas] = useState([]);
    const [newVillas, setNewVillas] = useState([]);
    const [errorMsg, setErrorMsg] = useState("");
    const [deletedVillaIds, setDeletedVillaIds] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        adminApiFetch(`${baseUrl}/villas`)
            .then(res => res.json())
            .then(data => {
                const normalizedVillas = normalizeVillas(data);
                setVillas(normalizedVillas);
                setEditedVillas(normalizedVillas.map(v => ({ ...v })));
            })
            .catch(() => setVillas([]));
    }, [baseUrl]);

    const handleChange = (idx, field, value) => {
        const updated = [...editedVillas];
        updated[idx][field] = value;
        setEditedVillas(updated);
    };

    const handleNewVillaChange = (idx, field, value) => {
        const updated = [...newVillas];
        updated[idx][field] = value;
        setNewVillas(updated);
    };

    const handleAddNewVillaRow = () => {
        setNewVillas([
            ...newVillas,
            {
                name: "", street: "", house_number: "", postal_code: "",
                location: "", country: "", notes: "", time_slot_interval_minutes: ""
            }
        ]);
    };

    const isVillaChanged = (original, edited) => {
        const fields = [
            "name", "street", "house_number", "postal_code",
            "location", "country", "notes", "time_slot_interval_minutes"
        ];
        return fields.some(field => (original[field] || "") !== (edited[field] || ""));
    };

    const isAnyRequiredFieldEmpty = () => {
        for (const villa of editedVillas) {
            if (!villa.name || !villa.time_slot_interval_minutes) return true;
        }
        for (const villa of newVillas) {
            if (!villa.name || !villa.time_slot_interval_minutes) return true;
        }
        return false;
    };

    const handleSave = async () => {
        if (isSaving) return;
        setErrorMsg("");
        const changedVillas = editedVillas.filter((edited, idx) => {
            const original = villas[idx];
            return isVillaChanged(original, edited);
        });
        const changedVillasForSave = changedVillas.filter(v => !deletedVillaIds.includes(v.id));
        const villasToDelete = villas.filter(v => deletedVillaIds.includes(v.id));

        const handleResponse = async (res, defaultError) => {
            if (res.ok) return res.status === 204 ? null : res.json();
            let err;
            try {
                err = await res.json();
            } catch {
                throw new Error(defaultError);
            }
            throw new Error(err.detail?.[0]?.msg || err.detail || defaultError);
        };

        try {
            setIsSaving(true);
            if (villasToDelete.length > 0) {
                const deleteRes = await adminApiFetch(`${baseUrl}/villas`, {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(villasToDelete)
                });
                await handleResponse(deleteRes, "Error while deleting");
            }

            if (changedVillasForSave.length > 0) {
                const updateRes = await adminApiFetch(`${baseUrl}/villas`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(changedVillasForSave)
                });
                await handleResponse(updateRes, "Error while updating");
            }

            if (newVillas.length > 0) {
                const createRes = await adminApiFetch(`${baseUrl}/villas`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(newVillas)
                });
                await handleResponse(createRes, "Error while creating");
            }

            const reload = await adminApiFetch(`${baseUrl}/villas`);
            const data = await reload.json();
            const normalizedVillas = normalizeVillas(data);
            setVillas(normalizedVillas);
            setEditedVillas(normalizedVillas.map(v => ({ ...v })));
            setNewVillas([]);
            setDeletedVillaIds([]);
        } catch (err) {
            setErrorMsg(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleRemoveNewVillaRow = (idx) => {
        setNewVillas(newVillas.filter((_, i) => i !== idx));
    };

    const handleMarkVillaForDelete = (id) => {
        if (deletedVillaIds.includes(id)) {
            setDeletedVillaIds(deletedVillaIds.filter(delId => delId !== id));
        } else {
            setDeletedVillaIds([...deletedVillaIds, id]);
        }
    };

    const changedCount = editedVillas.reduce((count, villa, idx) => {
        const original = villas[idx];
        if (!original || deletedVillaIds.includes(villa.id)) return count;
        return isVillaChanged(original, villa) ? count + 1 : count;
    }, 0);

    const pendingCount = changedCount + newVillas.length + deletedVillaIds.length;

    return (
        <div className="management-page">
            <div className="management-page-header">
                <h2 className="management-page-title">Villa Dashboard</h2>
                <div className="management-badge-row">
                    <span className="management-badge management-badge-neutral">Total: {editedVillas.length}</span>
                    <span className="management-badge management-badge-new">New: {newVillas.length}</span>
                    <span className="management-badge management-badge-changed">Changed: {changedCount}</span>
                    <span className="management-badge management-badge-delete">Delete: {deletedVillaIds.length}</span>
                    <span className="management-badge management-badge-neutral">Pending: {pendingCount}</span>
                </div>
            </div>

            <div className="management-callout">
                The fields Name and Interval (Minutes) are required. Changes are applied after clicking "Save all Changes".
            </div>

            {errorMsg && (
                <div className="management-error">{errorMsg}</div>
            )}

            <div className="management-table-shell">
            <table className="management-table">
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Name*</th>
                        <th>Street</th>
                        <th>House Number</th>
                        <th>Postal Code</th>
                        <th>Location</th>
                        <th>Country</th>
                        <th>Notes</th>
                        <th>Interval (Minutes)*</th>
                        <th></th>

                    </tr>
                </thead>
                <tbody>
                    {editedVillas.map((villa, idx) => {
                        const isDeleted = deletedVillaIds.includes(villa.id);
                        const isChanged = !isDeleted && isVillaChanged(villas[idx], villa);
                        const rowClass = isDeleted
                            ? "management-table-row-delete"
                            : (isChanged ? "management-table-row-changed" : "");

                        return (
                        <tr key={villa.id} className={rowClass}>
                            <td>
                                <div className="management-row-id">
                                    <span>{villa.id}</span>
                                    {isChanged && <span className="management-badge management-badge-changed">changed</span>}
                                    {isDeleted && <span className="management-badge management-badge-delete">delete</span>}
                                </div>
                            </td>
                            <td>
                                <input name="name" value={villa.name || ""} onChange={e => handleChange(idx, "name", e.target.value)} disabled={isDeleted} />
                            </td>
                            <td>
                                <input name="street" value={villa.street || ""} onChange={e => handleChange(idx, "street", e.target.value)} disabled={isDeleted} />
                            </td>
                            <td>
                                <input name="house_number" value={villa.house_number || ""} onChange={e => handleChange(idx, "house_number", e.target.value)} disabled={isDeleted} />
                            </td>
                            <td>
                                <input name="postal_code" value={villa.postal_code || ""} onChange={e => handleChange(idx, "postal_code", e.target.value)} disabled={isDeleted} />
                            </td>
                            <td>
                                <input name="location" value={villa.location || ""} onChange={e => handleChange(idx, "location", e.target.value)} disabled={isDeleted} />
                            </td>
                            <td>
                                <input name="country" value={villa.country || ""} onChange={e => handleChange(idx, "country", e.target.value)} disabled={isDeleted} />
                            </td>
                            <td>
                                <input name="notes" value={villa.notes || ""} onChange={e => handleChange(idx, "notes", e.target.value)} disabled={isDeleted} />
                            </td>
                            <td>
                                <select
                                    name="time_slot_interval_minutes"
                                    value={villa.time_slot_interval_minutes || ""}
                                    onChange={e => handleChange(idx, "time_slot_interval_minutes", e.target.value)}
                                    disabled={isDeleted}
                                >
                                    <option value="">Choose order interval</option>
                                    <option value="15">15</option>
                                    <option value="30">30</option>
                                </select>
                            </td>
                            <td>
                                <button
                                    type="button"
                                    onClick={() => handleMarkVillaForDelete(villa.id)}
                                    className="management-action-btn"
                                    aria-label="Delete villa / row"
                                    title={isDeleted ? "Undo delete villa / row" : "Delete villa / row"}
                                >
                                    {isDeleted ? "↺" : "✖"}
                                </button>
                            </td>
                        </tr>
                    );})}
                    {newVillas.map((villa, idx) => (
                        <tr key={`new-${idx}`} className="management-table-row-new">
                            <td>
                                <div className="management-row-id">
                                    <span>new</span>
                                    <span className="management-badge management-badge-new">new</span>
                                </div>
                            </td>
                            <td>
                                <input name="name" value={villa.name} onChange={e => handleNewVillaChange(idx, "name", e.target.value)} />
                            </td>
                            <td>
                                <input name="street" value={villa.street} onChange={e => handleNewVillaChange(idx, "street", e.target.value)} />
                            </td>
                            <td>
                                <input name="house_number" value={villa.house_number} onChange={e => handleNewVillaChange(idx, "house_number", e.target.value)} />
                            </td>
                            <td>
                                <input name="postal_code" value={villa.postal_code} onChange={e => handleNewVillaChange(idx, "postal_code", e.target.value)} />
                            </td>
                            <td>
                                <input name="location" value={villa.location} onChange={e => handleNewVillaChange(idx, "location", e.target.value)} />
                            </td>
                            <td>
                                <input name="country" value={villa.country} onChange={e => handleNewVillaChange(idx, "country", e.target.value)} />
                            </td>
                            <td>
                                <input name="notes" value={villa.notes} onChange={e => handleNewVillaChange(idx, "notes", e.target.value)} />
                            </td>
                            <td>
                                <select
                                    name="time_slot_interval_minutes"
                                    value={villa.time_slot_interval_minutes || ""}
                                    onChange={e => handleNewVillaChange(idx, "time_slot_interval_minutes", e.target.value)}
                                >
                                    <option value="">Order interval</option>
                                    <option value="15">15</option>
                                    <option value="30">30</option>
                                </select>
                            </td>
                            <td>
                                <button
                                    type="button"
                                    onClick={() => handleRemoveNewVillaRow(idx)}
                                    className="management-action-btn"
                                    aria-label="Delete villa / row"
                                    title="Delete villa / row"
                                >
                                    &#10006;
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>

            <div className="management-action-bar">
            <button
                onClick={handleAddNewVillaRow}
                disabled={isAnyRequiredFieldEmpty() || isSaving}
            >
                Create new Villa
            </button>
            <button
                onClick={handleSave}
                disabled={isAnyRequiredFieldEmpty() || isSaving}
            >
                {isSaving ? "Saving..." : "Save all Changes"}
            </button>
            </div>
        </div>
    );
}

export default Villas;